"use client"

import type React from "react"
import { useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { useFieldData } from "../../../hooks/useFieldData"
import { useFieldMapping } from "../../../hooks/useFieldMapping"
import { FIELD_MAPPING_CONSTANTS } from "../../../constants/field-mapping"
import { useToast } from "../../../context/ToastContext"

// Components
import LoadingScreen from "../shared/LoadingScreen"
import ErrorScreen from "../shared/ErrorScreen"
import StepIndicator from "../shared/StepIndicator"
import FieldMappingRow from "./FieldMappingRow"
import MappingSummary from "./MappingSummary"

import "./MapFields.css"

const MapFieldsConnectScreen: React.FC = () => {
  const navigate = useNavigate()
  const { hubspotFields, salesforceFields, loading, error, fetchFieldData } = useFieldData()
  const { error : errorAlert} = useToast()

  const {
    fieldMappings,
    updateHubSpotField,
    updateSalesforceField,
    deleteMapping,
    addMapping,
    validateMappings,
    saveMappings,
    setFieldMappings,
  } = useFieldMapping()

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      const initialMappings = await fetchFieldData()
      setFieldMappings(initialMappings)
    }
    loadData()
  }, [fetchFieldData, setFieldMappings])

  const handleAddFieldMapping = useCallback(() => {
    addMapping(hubspotFields, salesforceFields)
  }, [addMapping, hubspotFields, salesforceFields])

  const handleBack = useCallback(() => {
    navigate(FIELD_MAPPING_CONSTANTS.ROUTES.CHOOSE_SYNC)
  }, [navigate])

  const handleContinue = useCallback(() => {
    const validation = validateMappings()

    if (!validation.isValid) {
      // Show first error as alert (could be replaced with proper toast notification)
      errorAlert(validation.errors[0])
      return
    }

    if (saveMappings()) {
      navigate(FIELD_MAPPING_CONSTANTS.ROUTES.SYNC_DIRECTION)
    } else {
      alert("Failed to save field mappings. Please try again.")
    }
  }, [validateMappings, fieldMappings, saveMappings, navigate])

  // Render loading screen
  if (loading) {
    return <LoadingScreen title="Loading Field Mappings" description="Please wait while we fetch your field data..." />
  }

  // Render error screen
  if (error) {
    return <ErrorScreen title="Unable to Load Fields" message={error} onRetry={fetchFieldData} />
  }
  const getObjectName = (name: string) =>{
    const objectName = localStorage.getItem(name) || 'contact';
   return objectName.replace(objectName.charAt(0),objectName.charAt(0).toUpperCase())|| 'Contact'
  }

  const canAddMapping = hubspotFields.length > 0 && salesforceFields.length > 0

  return (
    <div className="map-fields">
      <div className="map-fields-header">
        <h1>Map Your Fields</h1>
        <p>Match fields between Salesforce and HubSpot to ensure your data syncs accurately.</p>
        <p>You can revisit these mappings later.</p>
        <StepIndicator currentStep={4} stepLabel="Map Fields" completedSteps={[1, 2, 3]} />
      </div>

      <div className="mapping-section">
        <div className="section-header">
          <h2>{getObjectName('source_object')}</h2>
          <p>
            Map contact fields to lead fields to define how your HubSpot contact data will appear in Salesforce leads.
          </p>
        </div>

        <div className="field-mapping-container">
          <div className="field-headers">
            <div className="header-left">
              <h3>{getObjectName('source_object')} Fields - HubSpot ({hubspotFields.length})</h3>
            </div>
            <div className="header-right">
              <h3>{getObjectName('target_object')} Fields - Salesforce ({salesforceFields.length})</h3>
            </div>
          </div>

          {fieldMappings.length === 0 ? (
            <div className="no-mappings">
              <p>No field mappings available. Click "Add Field Mapping" to create one.</p>
            </div>
          ) : (
            <div className="field-mappings">
              {fieldMappings.map((mapping) => (
                <FieldMappingRow
                  key={mapping.id}
                  mapping={mapping}
                  hubspotFields={hubspotFields}
                  salesforceFields={salesforceFields}
                  onHubSpotFieldChange={updateHubSpotField}
                  onSalesforceFieldChange={updateSalesforceField}
                  onDelete={deleteMapping}
                />
              ))}
            </div>
          )}

          <div className="add-mapping-section">
            <button className="add-mapping-btn" onClick={handleAddFieldMapping} disabled={!canAddMapping}>
              + Add Field Mapping
            </button>
            {!canAddMapping && <p className="add-mapping-disabled-text">Cannot add mappings: No fields available</p>}
          </div>
        </div>
      </div>

      <MappingSummary
        totalMappings={fieldMappings.length}
        hubspotFieldsCount={hubspotFields.length}
        salesforceFieldsCount={salesforceFields.length}
      />

      <div className="actions-section">
        <button className="back-button" onClick={handleBack}>
          Back
        </button>
        <button className="continue-button" onClick={handleContinue} disabled={fieldMappings.length === 0}>
          Continue ({fieldMappings.length} mappings)
        </button>
      </div>
    </div>
  )
}

export default MapFieldsConnectScreen
